import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ChevronDown, Menu, X } from 'lucide-react';
import './Header.css';

const Header = () => {
  const [locationOpen, setLocationOpen] = useState(false);
  const [servicesOpen, setServicesOpen] = useState(false);
  const [infoOpen, setInfoOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [mobileLocationOpen, setMobileLocationOpen] = useState(false);
  const [mobileServicesOpen, setMobileServicesOpen] = useState(false);
  const [mobileInfoOpen, setMobileInfoOpen] = useState(false);
  const location = useLocation();
  const mobileMenuRef = useRef(null);
  const hamburgerRef = useRef(null);

  const isActive = (path) => location.pathname === path;
  
  // Pages that should have the floating pill header with image background
  const hasHeroImage = ['/', '/about', '/contact', '/bookings', '/faqs', '/services', 
    '/locations/coffs-harbour', '/locations/telehealth', '/clinics',
    '/visiting-clinics/hamilton', '/visiting-clinics/port-macquarie',
    '/visiting-clinics/sydney', '/visiting-clinics/melbourne', '/visiting-clinics/gold-coast',
    '/privacy-policy', '/terms-and-conditions', '/refer',
    '/autism-assessment', '/adhd-assessment', '/fasd-assessment', '/learning-assessment',
    '/what-to-expect', '/therapy', '/schools', '/referrers', '/fees'].includes(location.pathname);
  
  const closeMobileMenu = () => {
    setMobileMenuOpen(false);
    setMobileLocationOpen(false);
    setMobileServicesOpen(false);
    setMobileInfoOpen(false);
  };

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (mobileMenuOpen && 
          mobileMenuRef.current && 
          !mobileMenuRef.current.contains(event.target) &&
          hamburgerRef.current &&
          !hamburgerRef.current.contains(event.target)) {
        closeMobileMenu();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('touchstart', handleClickOutside);
    
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
    };
  }, [mobileMenuOpen]);

  return (
    <header className={`header-nav ${hasHeroImage ? 'header-transparent' : ''}`}>
      <div className="container header-content">
        {/* Hamburger Menu - Always visible on transparent header */}
        {hasHeroImage && (
          <button 
            ref={hamburgerRef}
            className="hamburger-menu-left menu-text-button"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            aria-label="Toggle menu"
          >
            MENU
          </button>
        )}
        
        <Link to="/" className="logo-link" onClick={closeMobileMenu}>
          <img 
            src={hasHeroImage 
              ? "/logo.png"
              : "/images/logos/clinical-minds-logo-white.png"
            }
            alt="Psychology Practice Logo" 
            className="logo-image"
          />
        </Link>
        
        {/* Contact Text - Right aligned, text only on transparent header */}
        {hasHeroImage && (
          <Link to="/contact" className="contact-text" onClick={closeMobileMenu}>
            CONTACT
          </Link>
        )}
        
        {/* Desktop Navigation - Only show on non-hero pages */}
        {!hasHeroImage && (
          <>
            <nav className="nav-menu">
          <Link 
            to="/" 
            className={`nav-link ${isActive('/') ? 'active' : ''}`}
          >
            Home
          </Link>
          
          <Link 
            to="/about" 
            className={`nav-link ${isActive('/about') ? 'active' : ''}`}
          >
            About
          </Link>
          
          <div 
            className="nav-dropdown"
            onMouseEnter={() => setServicesOpen(true)}
            onMouseLeave={() => setServicesOpen(false)}
          >
            <Link to="/services" className={`nav-link dropdown-trigger ${['/services','/autism-assessment','/adhd-assessment','/fasd-assessment','/learning-assessment','/what-to-expect','/therapy','/schools','/referrers','/fees'].includes(location.pathname) ? 'active' : ''}`}>
              Services <ChevronDown size={16} />
            </Link>
            {servicesOpen && (
              <div className="dropdown-menu">
                <Link to="/services" className="dropdown-item">All Services</Link>
                <Link to="/autism-assessment" className="dropdown-item">Autism Assessment</Link>
                <Link to="/adhd-assessment" className="dropdown-item">ADHD Assessment</Link>
                <Link to="/fasd-assessment" className="dropdown-item">FASD Assessment</Link>
                <Link to="/learning-assessment" className="dropdown-item">Learning Assessment</Link>
                <Link to="/what-to-expect" className="dropdown-item">What to Expect</Link>
                <Link to="/therapy" className="dropdown-item">Therapy</Link>
                <Link to="/fees" className="dropdown-item">Fees & Rebates</Link>
              </div>
            )}
          </div>
          
          <Link 
            to="/refer" 
            className={`nav-link ${isActive('/refer') ? 'active' : ''}`}
          >
            Refer
          </Link>
          
          <div 
            className="nav-dropdown"
            onMouseEnter={() => setLocationOpen(true)}
            onMouseLeave={() => setLocationOpen(false)}
          >
            <button className="nav-link dropdown-trigger">
              Locations <ChevronDown size={16} />
            </button>
            {locationOpen && (
              <div className="dropdown-menu">
                <Link to="/locations/coffs-harbour" className="dropdown-item">Coffs Harbour</Link>
                <Link to="/clinics" className="dropdown-item">Visiting Clinics</Link>
                <Link to="/visiting-clinics/port-macquarie" className="dropdown-item dropdown-sub">Port Macquarie</Link>
                <Link to="/visiting-clinics/sydney" className="dropdown-item dropdown-sub">Sydney</Link>
                <Link to="/visiting-clinics/melbourne" className="dropdown-item dropdown-sub">Melbourne</Link>
                <Link to="/visiting-clinics/gold-coast" className="dropdown-item dropdown-sub">Gold Coast</Link>
                <Link to="/visiting-clinics/hamilton" className="dropdown-item dropdown-sub">Hamilton VIC</Link>
                <Link to="/locations/telehealth" className="dropdown-item">Telehealth</Link>
              </div>
            )}
          </div>
          
          <Link 
            to="/faqs" 
            className={`nav-link ${isActive('/faqs') ? 'active' : ''}`}
          >
            FAQs
          </Link>
          
          <Link 
            to="/contact" 
            className={`nav-link ${isActive('/contact') ? 'active' : ''}`}
          >
            Contact
          </Link>
        </nav>
        
        <div className="header-actions">
          <Link to="/bookings" className="btn-primary header-cta">
            Request Appointment
          </Link>
          
          {/* Mobile Menu Toggle */}
          <button 
            className="mobile-menu-toggle"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            aria-label="Toggle menu"
          >
            {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
          </>
        )}
      </div>
      
      {/* Mobile Menu */}
      {mobileMenuOpen && (
        <div className="mobile-menu" ref={mobileMenuRef}>
          <div className="mobile-menu-content">
            <Link 
              to="/" 
              className={`mobile-nav-link ${isActive('/') ? 'active' : ''}`}
              onClick={closeMobileMenu}
            >
              Home
            </Link>
            
            <Link 
              to="/about" 
              className={`mobile-nav-link ${isActive('/about') ? 'active' : ''}`}
              onClick={closeMobileMenu}
            >
              About
            </Link>
            
            <div className="mobile-dropdown">
              <button 
                className="mobile-nav-link dropdown-trigger"
                onClick={() => setMobileServicesOpen(!mobileServicesOpen)}
              >
                Services <ChevronDown size={16} className={mobileServicesOpen ? 'rotated' : ''} />
              </button>
              {mobileServicesOpen && (
                <div className="mobile-dropdown-content">
                  <Link to="/services" className="mobile-dropdown-item" onClick={closeMobileMenu}>
                    All Services
                  </Link>
                  <Link to="/autism-assessment" className="mobile-dropdown-item" onClick={closeMobileMenu}>
                    Autism Assessment
                  </Link>
                  <Link to="/adhd-assessment" className="mobile-dropdown-item" onClick={closeMobileMenu}>
                    ADHD Assessment
                  </Link>
                  <Link to="/fasd-assessment" className="mobile-dropdown-item" onClick={closeMobileMenu}>
                    FASD Assessment
                  </Link>
                  <Link to="/learning-assessment" className="mobile-dropdown-item" onClick={closeMobileMenu}>
                    Learning Assessment
                  </Link>
                  <Link to="/what-to-expect" className="mobile-dropdown-item" onClick={closeMobileMenu}>
                    What to Expect
                  </Link>
                  <Link to="/therapy" className="mobile-dropdown-item" onClick={closeMobileMenu}>
                    Therapy
                  </Link>
                  <Link to="/fees" className="mobile-dropdown-item" onClick={closeMobileMenu}>
                    Fees & Rebates
                  </Link>
                </div>
              )}
            </div>
            
            <Link 
              to="/refer" 
              className={`mobile-nav-link ${isActive('/refer') ? 'active' : ''}`}
              onClick={closeMobileMenu}
            >
              Refer
            </Link>
            
            <div className="mobile-dropdown">
              <button 
                className="mobile-nav-link dropdown-trigger"
                onClick={() => setMobileLocationOpen(!mobileLocationOpen)}
              >
                Locations <ChevronDown size={16} className={mobileLocationOpen ? 'rotated' : ''} />
              </button>
              {mobileLocationOpen && (
                <div className="mobile-dropdown-content">
                  <Link to="/locations/coffs-harbour" className="mobile-dropdown-item" onClick={closeMobileMenu}>
                    Coffs Harbour
                  </Link>
                  <Link to="/clinics" className="mobile-dropdown-item" onClick={closeMobileMenu}>
                    Visiting Clinics
                  </Link>
                  <Link to="/visiting-clinics/port-macquarie" className="mobile-dropdown-item mobile-dropdown-sub" onClick={closeMobileMenu}>
                    Port Macquarie
                  </Link>
                  <Link to="/visiting-clinics/sydney" className="mobile-dropdown-item mobile-dropdown-sub" onClick={closeMobileMenu}>
                    Sydney
                  </Link>
                  <Link to="/visiting-clinics/melbourne" className="mobile-dropdown-item mobile-dropdown-sub" onClick={closeMobileMenu}>
                    Melbourne
                  </Link>
                  <Link to="/visiting-clinics/gold-coast" className="mobile-dropdown-item mobile-dropdown-sub" onClick={closeMobileMenu}>
                    Gold Coast
                  </Link>
                  <Link to="/visiting-clinics/hamilton" className="mobile-dropdown-item mobile-dropdown-sub" onClick={closeMobileMenu}>
                    Hamilton VIC
                  </Link>
                  <Link to="/locations/telehealth" className="mobile-dropdown-item" onClick={closeMobileMenu}>
                    Telehealth
                  </Link>
                </div>
              )}
            </div>
            
            <Link 
              to="/faqs" 
              className={`mobile-nav-link ${isActive('/faqs') ? 'active' : ''}`}
              onClick={closeMobileMenu}
            >
              FAQs
            </Link>
            
            <Link 
              to="/bookings" 
              className={`mobile-nav-link ${isActive('/bookings') ? 'active' : ''}`}
              onClick={closeMobileMenu}
            >
              Request Appointment
            </Link>
            
            <Link 
              to="/contact" 
              className={`mobile-nav-link ${isActive('/contact') ? 'active' : ''}`}
              onClick={closeMobileMenu}
            >
              Contact
            </Link>
          </div>
        </div>
      )}
    </header>
  );
};

export default Header;
